import { pack } from '@attain-apps/data-utils'
import * as errors from '@attain-apps/errors'
import { Multirange } from '@attain-apps/range'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as models from '../../../../models'
import * as expression from '../../../expression'
import type { Mutation } from '../../../mutation'
import { Record } from '../../../record'
import type { Query } from '../../query'
import { Target } from '../../target'
import { Variable } from '../../variable'
import type { Context } from '../node/context'
import type { ForGroup } from '../node/for-group'
import { ForRecords } from '../node/for-records'
import { ForValue } from '../node/for-value'

import { getDefaultSelection } from './get-default-selection'

/**
 * Asserts that the input is either a {@link models.Entity.Id} or a
 * {@link models.Snapshot.Id}.
 */
// TODO: move this asserter to some common place
const entityOrSnapshotIdAsserter = TypeAsserter.Factory.from(
  TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.union(models.Entity.Id.isId, models.Snapshot.Id.isId),
  ),
)

/**
 * Creates a {@link ForGroup.Member}
 *
 * __NOTE:__ the returned node does not have to be used as a member of
 * {@link ForGroup}. This just returns something that qualifies as a
 * {@link ForGroup.Member}. Whether or not it is actually used as a member of a
 * group is determined elsewhere.
 */
export function createMemberNode<T>({
  expressionContext: partialExpressionContext,
  id,
  nodesCache,
  query,
  period,
  purpose,
  schema,
  subqueryNodes,
}: Readonly<createMemberNode.Params>): ForGroup.Member<T> {
  const { quantity, skip, sort } = query

  const expressionContext = { ...partialExpressionContext, queryNodeId: id }

  const selection = expression.node.from(
    query.selection ?? getDefaultSelection(query),
    expressionContext,
  )

  // Set the selection in the expression context so that it can be referenced
  // elsewhere in the expression (e.g. in filter or sort)
  expressionContext.siblingExpressionNodes[
    Variable.Name.Reserved.from(
      expression.fn.definitions.utils.CommonName.Selection,
    )
  ] = selection

  const assumption: readonly Mutation[] = pack(query.assumption).filter(
    TypeChecker.isNotNullish.raw,
  )

  let filter =
    query.filter !== undefined
      ? expression.node.from(query.filter, expressionContext)
      : undefined
  if (expression.node.ForValue.isForValue(filter)) {
    if (filter.value) {
      // filter = true is the same as not having a filter
      filter = undefined
    } else {
      // If the filter is false, we can return an empty result because
      // 'where false' will always return an empty result
      return new ForValue<Maybe.ReadonlyArray<T>>({
        id,
        nodesCache,
        query,
        value: [],
      })
    }
  } else if (filter && !expression.fn.node.Node.isNode(filter)) {
    // NOTE: as of 2/1/24, there are only node.ForValue and data.expression.fn.node.Node
    throw new errors.UnexpectedError()
  }

  if (
    purpose.include !== 'all' &&
    purpose.include.length === 0 &&
    purpose.nil === 'exclude'
  ) {
    // If no purposes are included and records that have no purpose are
    // excluded, then there are no eligible records, so the result will be an
    // empty list
    return new ForValue<Maybe.ReadonlyArray<T>>({
      id,
      nodesCache,
      query,
      value: [],
    })
  }

  const sortByNode =
    sort?.by !== undefined
      ? expression.fn.node.from(sort.by, expressionContext)
      : undefined

  const source: ForRecords.Source = expressionContext.listOfDateRanges
    ? {
        dateRangeList: expressionContext.listOfDateRanges,
      }
    : {
        modelId: TypeAsserter.validate(
          expressionContext.modelId,
          entityOrSnapshotIdAsserter,
        ),
      }

  return new ForRecords({
    assumption,
    filter,
    id,
    nodesCache,
    period,
    purpose,
    quantity,
    query,
    schema,
    selection,
    skip,
    // TODO:  if a query does include a sort property when targeting a list of
    // ranges, that sorting should be done on the list of ranges in TypeScript
    // before the node is created
    sort: Target.DateRange.List.isList(query.target)
      ? undefined
      : {
          // If we have a fn to sort by, we use it. Otherwise, we just sort by the
          // `!id` attribute. It is worth noting, that if we have a value node for
          // the sort by, it is going to be the same for every record, so it is
          // basically the same as not having anything to sort by
          by: expression.fn.node.Node.isNode(sortByNode)
            ? sortByNode
            : // NOTE: it is pretty safe to assume this will end up as a fn node since
              // we are referencing the ID attribute on each record, which cannot be
              // rendered to a value node ahead of tiem
              (expression.fn.node.from(
                { '@.': '!id' },
                expressionContext,
              ) satisfies expression.node.Node as expression.fn.node.Node<number>),
          descending: sort?.descending ?? false,
        },
    source,
    subqueryNodes,
  })
}
namespace createMemberNode {
  export interface Params
    extends Pick<ForRecords, 'period' | 'purpose'>,
      Pick<Context, 'schema' | 'nodesCache'> {
    expressionContext: Omit<expression.node.Context, 'queryNodeId'>
    id: string
    query: Query
    subqueryNodes: ForRecords.BindableSubqueryNodes
  }
}
